import React, { useEffect, useState, useRef } from 'react';
import { useInView } from 'framer-motion';

interface TypeWriterProps {
  words: string[];
  typeSpeed?: number;
  deleteSpeed?: number;
  pauseDuration?: number;
  style?: React.CSSProperties;
  className?: string;
}

const TypeWriter: React.FC<TypeWriterProps> = ({
  words,
  typeSpeed = 80,
  deleteSpeed = 50,
  pauseDuration = 2000,
  style,
  className,
}) => {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    const currentWord = words[wordIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setText(currentWord.slice(0, text.length + 1));
        if (text.length + 1 === currentWord.length) {
          setTimeout(() => setIsDeleting(true), pauseDuration);
          return;
        }
      } else {
        setText(currentWord.slice(0, text.length - 1));
        if (text.length === 0) {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, isDeleting ? deleteSpeed : typeSpeed);

    return () => clearTimeout(timeout);
  }, [text, isDeleting, wordIndex, words, typeSpeed, deleteSpeed, pauseDuration, isInView]);

  return (
    <span ref={ref} style={style} className={className}>
      {text}
      <span className="typewriter-cursor" />
    </span>
  );
};

export default TypeWriter;
